import { z } from "zod";
import Form from "./Form";
import FormInput from "@src/components/atoms/FormInput";
import Button from "@src/components/atoms/Button";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import { DropDown } from "@src/components/atoms";
import { useState } from "react";
import { useIsMobileWidth } from "@src/utils/mediaQuery";

const ProfileForm = ({
  title,
  onProfileSubmit,
  defaultValues,
  dropdownOptions,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const formSchema = z.object({
    firstname: z.string().optional().default(""),
    lastname: z.string().optional().default(""),
    commitment: z.string(),
  });
  const isMobile = useIsMobileWidth();

  const handleSubmit = async (data) => {
    setError("");
    setIsSubmitting(true);
    try {
      onProfileSubmit && (await onProfileSubmit(data));
    } catch (error) {
      setError("Failed to save changes. Please try again later.");
      setIsSubmitting(false);
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto p-6 ">
      {title && <h3 className=" mb-6 text-center">{title}</h3>}
      <Form
        onSubmit={(data) => handleSubmit(data)}
        schema={formSchema}
        options={{
          defaultValues: {
            firstname: defaultValues?.firstname ?? "",
            lastname: defaultValues?.lastname ?? "",
            commitment: defaultValues?.commitment ?? "",
          },
        }}
        className="space-y-6"
      >
        {(methods) => {
          methods.watch(["firstname", "lastname", "commitment"]);
          const hasChanged = methods.formState.isDirty;

          return (
            <>
              <div
                className={`${isMobile ? "flex flex-col justify-center items-center py-6" : "grid grid-cols-2"} gap-6 mb-4`}
              >
                <FormInput
                  label="First Name"
                  name="firstname"
                  type="text"
                  placeholder="John"
                  textColor={"primary"}
                  onChange={(e) => {
                    methods.setValue("firstname", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />

                <FormInput
                  label="Last Name"
                  name="lastname"
                  type="text"
                  placeholder="Harris"
                  textColor={"primary"}
                  onChange={(e) => {
                    methods.setValue("lastname", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
                <div
                  className={`${isMobile ? "flex flex-col w-full items-stretch" : "col-span-2"} text-left mb-4`}
                >
                  <p className="text-primary-500 text-sm font-bold mb-3">
                    Committed to
                  </p>

                  <DropDown
                    value={methods.watch("commitment")}
                    onChange={(e) => {
                      methods.setValue("commitment", e, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    options={dropdownOptions}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <div className="min-w-48">
                  <Button
                    padding={false}
                    submit
                    disabled={!hasChanged}
                    loading={isSubmitting}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </>
          );
        }}
      </Form>
      {error && <div>{error}</div>}
    </div>
  );
};

export const profileFormControls = {
  onProfileSubmit: {
    propType: PropTypes.func.isRequired,
    description: "Form submission handler from parent",
    disable: true,
  },
};

ProfileForm.propTypes = AppUtils.getPropTypeFromControls(profileFormControls);

export default ProfileForm;
